import { useState, type ReactElement } from "react";
import { MessageCircle, Sparkles, X } from "lucide-react";
import type { CoachingNudgeParams } from "@playbookai/widget-schemas";
import { Tooltip } from "../../molecules/Tooltip/Tooltip.js";
import { useWidgetActions } from "../widget-actions.js";
import type { WidgetBodyProps } from "../types.js";

/**
 * Coaching Nudge — AI dashboard widget body (data-blind). Shows ONE PlayerAI
 * nudge at a time (the first not dismissed); dismissing advances to the next
 * and is ephemeral local view state — no persistence (the oracle's
 * `localStorage` + day-of-year rotation are dropped for a deterministic,
 * data-blind body). The "Ask PlayerAI" affordance is gated on the host-
 * provided `useWidgetActions()` (the body never calls AIS itself, ADR-0007 /
 * 0012) and forwards the nudge's `askPrompt` intent seed. Bare content; the
 * `DashboardWidget` adapter supplies the `WidgetCard` frame.
 */
export function CoachingNudgeWidget({
  data,
}: WidgetBodyProps<CoachingNudgeParams>): ReactElement {
  const actions = useWidgetActions();
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());
  const nudges = data.nudges ?? [];
  const nudge = nudges.find((n) => !dismissed.has(n.id));

  if (!nudge) {
    return (
      <p className="text-dense-1 text-pb-text-muted italic">
        {nudges.length === 0
          ? "No nudges right now."
          : "Nudge dismissed for today."}
      </p>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <span className="shrink-0 w-8 h-8 rounded-full bg-pb-iris-soft shadow-glow-iris flex items-center justify-center">
        <Sparkles className="w-4 h-4 text-pb-iris-text" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold mb-0.5">
          PlayerAI · Coaching nudge
        </span>
        <span className="block text-sm text-pb-text leading-snug">
          {nudge.text}
        </span>
      </span>
      <span className="shrink-0 flex items-center gap-1">
        {/* Each hint repeats its button's name, so it describes nothing
            more. */}
        {actions?.askPlayerAI ? (
          <Tooltip content="Ask PlayerAI about this" describeChild={false}>
            {/* eslint-disable-next-line ds/no-hand-built -- kept: an outlined 28px icon button in iris ink; Button outline has no ai tone, and no approved look row lists the change. */}
            <button
              type="button"
              onClick={() => actions.askPlayerAI?.(nudge.askPrompt)}
              aria-label="Ask PlayerAI about this"
              className="w-7 h-7 flex items-center justify-center rounded-md border border-pb-control-border text-pb-iris-text hover:bg-pb-iris-soft/30 transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        ) : null}
        <Tooltip content="Dismiss" describeChild={false}>
          {/* eslint-disable-next-line ds/no-hand-built -- kept: a 28px icon button in muted ink; Button ghost rests in secondary ink, and no approved look row lists the change. */}
          <button
            type="button"
            onClick={() => setDismissed((prev) => new Set(prev).add(nudge.id))}
            aria-label="Dismiss this nudge"
            className="w-7 h-7 flex items-center justify-center rounded-md text-pb-text-muted hover:text-pb-text hover:pb-overlay-paint transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Tooltip>
      </span>
    </div>
  );
}
